import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, authHeaders, formatErr } from "./api";
import { TabCard } from "./fields";
import { History, Loader2, RotateCcw } from "lucide-react";

const SECTION_LABELS = {
  contact: "Contact & Business", prices: "Prices", work: "Work / Clients", faqs: "FAQs",
  quikpik: "Quikpik", announcement: "Announcement", restore: "Restore",
};

export default function VersionsTab({ token, onSaved, onUnauthorized }) {
  const [versions, setVersions] = useState(null);
  const [restoring, setRestoring] = useState("");

  const load = () => {
    axios
      .get(`${API}/content/versions`, authHeaders(token))
      .then((r) => setVersions(r.data.versions))
      .catch((err) => {
        if (err.response?.status === 401) return onUnauthorized();
        toast.error("Couldn't load versions.");
      });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async (id) => {
    if (!window.confirm("Restore this version? Current content will be replaced (a snapshot is saved first).")) return;
    setRestoring(id);
    try {
      const { data } = await axios.post(`${API}/content/versions/${id}/restore`, {}, authHeaders(token));
      onSaved(data);
      toast.success("Version restored — live on the website now.");
      load();
    } catch (err) {
      if (err.response?.status === 401) return onUnauthorized();
      toast.error(formatErr(err, "Restore failed."));
    } finally {
      setRestoring("");
    }
  };

  return (
    <TabCard title="Version History" sub="The last 20 saves — restore any of them" testid="tab-versions">
      {!versions ? (
        <p className="flex items-center gap-2 text-sm text-mut"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : versions.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-mut" data-testid="versions-empty"><History size={14} className="text-faint" /> Nothing saved yet — versions appear after your first save.</p>
      ) : (
        <ul className="divide-y divide-line border-y border-line">
          {versions.map((v) => (
            <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`version-${v.id}`}>
              <div>
                <p className="text-sm text-txt">{SECTION_LABELS[v.section] || v.section}</p>
                <p className="mt-0.5 text-xs text-faint">{new Date(v.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
              </div>
              <button
                onClick={() => restore(v.id)}
                disabled={restoring === v.id}
                data-testid={`restore-${v.id}`}
                className="flex items-center gap-2 border border-line px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop disabled:opacity-60"
              >
                {restoring === v.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </TabCard>
  );
}
